/**
 * Recording Context
 *
 * Manages state for guided video recording including camera, MediaPipe pose detection,
 * and recording status.
 */

import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { PoseLandmarkerResult } from '@mediapipe/tasks-vision';
import { FilmingAngle } from '../common/deviceOrientationUtilities';

// Guidance message types
export interface GuidanceMessage {
  type: 'success' | 'warning' | 'error' | 'info';
  message: string;
  priority: number; // 1 = highest, 4 = lowest
}

// Recording state interface
export interface RecordingState {
  // Camera state
  cameraStream: MediaStream | null;
  cameraPermissionGranted: boolean;
  cameraError: string | null;

  // Recording state
  isRecording: boolean;
  isPaused: boolean;
  recordingDuration: number; // milliseconds
  recordedBlob: Blob | null;

  // Pose detection state
  poseDetectionActive: boolean;
  currentPose: PoseLandmarkerResult | null;
  poseDetectionError: string | null;

  // Guidance state
  currentGuidance: GuidanceMessage[];
  infantDetected: boolean;
  framingQuality: 'good' | 'needs_adjustment' | 'poor' | 'unknown';
  infantOrientation: 'supine' | 'prone' | 'side' | 'unknown';

  // Device orientation state
  deviceOrientation: {
    beta: number | null;
    filmingAngle: FilmingAngle;
    isLandscape: boolean;
  };

  // UI state
  isInitializing: boolean;
  error: string | null;
}

// Action types
export enum RecordingActionType {
  // Camera actions
  SET_CAMERA_STREAM = 'SET_CAMERA_STREAM',
  SET_CAMERA_PERMISSION = 'SET_CAMERA_PERMISSION',
  SET_CAMERA_ERROR = 'SET_CAMERA_ERROR',

  // Recording actions
  START_RECORDING = 'START_RECORDING',
  STOP_RECORDING = 'STOP_RECORDING',
  PAUSE_RECORDING = 'PAUSE_RECORDING',
  RESUME_RECORDING = 'RESUME_RECORDING',
  UPDATE_RECORDING_DURATION = 'UPDATE_RECORDING_DURATION',
  SET_RECORDED_BLOB = 'SET_RECORDED_BLOB',

  // Pose detection actions
  SET_POSE_DETECTION_ACTIVE = 'SET_POSE_DETECTION_ACTIVE',
  UPDATE_CURRENT_POSE = 'UPDATE_CURRENT_POSE',
  SET_POSE_DETECTION_ERROR = 'SET_POSE_DETECTION_ERROR',

  // Guidance actions
  UPDATE_GUIDANCE = 'UPDATE_GUIDANCE',
  SET_INFANT_DETECTED = 'SET_INFANT_DETECTED',
  SET_FRAMING_QUALITY = 'SET_FRAMING_QUALITY',
  SET_INFANT_ORIENTATION = 'SET_INFANT_ORIENTATION',

  // Device orientation actions
  UPDATE_DEVICE_ORIENTATION = 'UPDATE_DEVICE_ORIENTATION',

  // General actions
  SET_INITIALIZING = 'SET_INITIALIZING',
  SET_ERROR = 'SET_ERROR',
  RESET_STATE = 'RESET_STATE'
}

// Action interfaces
export type RecordingAction =
  | { type: RecordingActionType.SET_CAMERA_STREAM; payload: MediaStream | null }
  | { type: RecordingActionType.SET_CAMERA_PERMISSION; payload: boolean }
  | { type: RecordingActionType.SET_CAMERA_ERROR; payload: string | null }
  | { type: RecordingActionType.START_RECORDING }
  | { type: RecordingActionType.STOP_RECORDING }
  | { type: RecordingActionType.PAUSE_RECORDING }
  | { type: RecordingActionType.RESUME_RECORDING }
  | { type: RecordingActionType.UPDATE_RECORDING_DURATION; payload: number }
  | { type: RecordingActionType.SET_RECORDED_BLOB; payload: Blob | null }
  | { type: RecordingActionType.SET_POSE_DETECTION_ACTIVE; payload: boolean }
  | { type: RecordingActionType.UPDATE_CURRENT_POSE; payload: PoseLandmarkerResult | null }
  | { type: RecordingActionType.SET_POSE_DETECTION_ERROR; payload: string | null }
  | { type: RecordingActionType.UPDATE_GUIDANCE; payload: GuidanceMessage[] }
  | { type: RecordingActionType.SET_INFANT_DETECTED; payload: boolean }
  | { type: RecordingActionType.SET_FRAMING_QUALITY; payload: 'good' | 'needs_adjustment' | 'poor' | 'unknown' }
  | { type: RecordingActionType.SET_INFANT_ORIENTATION; payload: 'supine' | 'prone' | 'side' | 'unknown' }
  | { type: RecordingActionType.UPDATE_DEVICE_ORIENTATION; payload: { beta: number | null; filmingAngle: FilmingAngle; isLandscape: boolean } }
  | { type: RecordingActionType.SET_INITIALIZING; payload: boolean }
  | { type: RecordingActionType.SET_ERROR; payload: string | null }
  | { type: RecordingActionType.RESET_STATE };

// Initial state
const initialState: RecordingState = {
  cameraStream: null,
  cameraPermissionGranted: false,
  cameraError: null,
  isRecording: false,
  isPaused: false,
  recordingDuration: 0,
  recordedBlob: null,
  poseDetectionActive: false,
  currentPose: null,
  poseDetectionError: null,
  currentGuidance: [],
  infantDetected: false,
  framingQuality: 'unknown',
  infantOrientation: 'unknown',
  deviceOrientation: {
    beta: null,
    filmingAngle: 'unknown',
    isLandscape: false
  },
  isInitializing: false,
  error: null
};

// Reducer function
function recordingReducer(state: RecordingState, action: RecordingAction): RecordingState {
  switch (action.type) {
    case RecordingActionType.SET_CAMERA_STREAM:
      return { ...state, cameraStream: action.payload };

    case RecordingActionType.SET_CAMERA_PERMISSION:
      return { ...state, cameraPermissionGranted: action.payload };

    case RecordingActionType.SET_CAMERA_ERROR:
      return { ...state, cameraError: action.payload };

    case RecordingActionType.START_RECORDING:
      return {
        ...state,
        isRecording: true,
        isPaused: false,
        recordingDuration: 0,
        recordedBlob: null
      };

    case RecordingActionType.STOP_RECORDING:
      return { ...state, isRecording: false, isPaused: false };

    case RecordingActionType.PAUSE_RECORDING:
      return { ...state, isPaused: true };

    case RecordingActionType.RESUME_RECORDING:
      return { ...state, isPaused: false };

    case RecordingActionType.UPDATE_RECORDING_DURATION:
      return { ...state, recordingDuration: action.payload };

    case RecordingActionType.SET_RECORDED_BLOB:
      return { ...state, recordedBlob: action.payload };

    case RecordingActionType.SET_POSE_DETECTION_ACTIVE:
      return { ...state, poseDetectionActive: action.payload };

    case RecordingActionType.UPDATE_CURRENT_POSE:
      return { ...state, currentPose: action.payload };

    case RecordingActionType.SET_POSE_DETECTION_ERROR:
      return { ...state, poseDetectionError: action.payload };

    case RecordingActionType.UPDATE_GUIDANCE:
      return { ...state, currentGuidance: action.payload };

    case RecordingActionType.SET_INFANT_DETECTED:
      return { ...state, infantDetected: action.payload };

    case RecordingActionType.SET_FRAMING_QUALITY:
      return { ...state, framingQuality: action.payload };

    case RecordingActionType.SET_INFANT_ORIENTATION:
      return { ...state, infantOrientation: action.payload };

    case RecordingActionType.UPDATE_DEVICE_ORIENTATION:
      return { ...state, deviceOrientation: action.payload };

    case RecordingActionType.SET_INITIALIZING:
      return { ...state, isInitializing: action.payload };

    case RecordingActionType.SET_ERROR:
      return { ...state, error: action.payload };

    case RecordingActionType.RESET_STATE:
      return initialState;

    default:
      return state;
  }
}

// Context creation
const RecordingStateContext = createContext<RecordingState | undefined>(undefined);
const RecordingDispatchContext = createContext<React.Dispatch<RecordingAction> | undefined>(undefined);

// Provider component
interface RecordingProviderProps {
  children: ReactNode;
}

export function RecordingProvider({ children }: RecordingProviderProps) {
  const [state, dispatch] = useReducer(recordingReducer, initialState);

  return (
    <RecordingStateContext.Provider value={state}>
      <RecordingDispatchContext.Provider value={dispatch}>
        {children}
      </RecordingDispatchContext.Provider>
    </RecordingStateContext.Provider>
  );
}

// Custom hooks for using context
export function useRecordingState(): RecordingState {
  const context = useContext(RecordingStateContext);
  if (context === undefined) {
    throw new Error('useRecordingState must be used within a RecordingProvider');
  }
  return context;
}

export function useRecordingDispatch(): React.Dispatch<RecordingAction> {
  const context = useContext(RecordingDispatchContext);
  if (context === undefined) {
    throw new Error('useRecordingDispatch must be used within a RecordingProvider');
  }
  return context;
}

// Convenience hook to use both state and dispatch
export function useRecording() {
  return {
    state: useRecordingState(),
    dispatch: useRecordingDispatch()
  };
}
